import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

type ListRowProps = {
  readonly onClick?: () => void;
  readonly leading?: ReactNode;
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  /**
   * A badge about the record (due date, low stock): under the subtitle on phones so the title
   * keeps its width, next to the trailing controls from `sm` up.
   */
  readonly status?: ReactNode;
  readonly trailing?: ReactNode;
  readonly muted?: boolean;
};

/** A row whose whole surface opens the record; leading/trailing controls stay clickable. */
export function ListRow({ onClick, leading, title, subtitle, status, trailing, muted }: ListRowProps) {
  return (
    <div className="group relative flex items-center gap-3 px-4 py-3 transition-colors focus-within:bg-muted/50 hover:bg-muted/50">
      {leading && <div className="relative z-10 shrink-0">{leading}</div>}
      <div className={cn('flex min-w-0 flex-1 flex-col gap-0.5', muted && 'opacity-60')}>
        {onClick ? (
          <button
            type="button"
            onClick={onClick}
            className="truncate text-left font-medium outline-none after:absolute after:inset-0 after:content-['']"
          >
            {title}
          </button>
        ) : (
          <span className="truncate font-medium">{title}</span>
        )}
        {subtitle && <div className="truncate text-sm text-muted-foreground">{subtitle}</div>}
        {status && <div className="flex flex-wrap gap-1 pt-0.5 empty:hidden sm:hidden">{status}</div>}
      </div>
      {(status || trailing) && (
        <div className="relative z-10 flex shrink-0 items-center gap-2">
          {status && <div className="flex items-center gap-1 empty:hidden max-sm:hidden">{status}</div>}
          {trailing}
        </div>
      )}
    </div>
  );
}
